import { useTranslations } from "../../contexts/Translation";
import { useState } from "react";
import { useApiData } from "../../contexts/ApiData";
import { Group, ValidationError } from "../../types";
import List from "../List/List";
import Input from "../Forms/Input";
import { H2 } from "../Headers/Headings";
import Button, { IconButton } from "../Button/Button";
import Link from "../Link/Link";
import { useOrganizations } from "../../contexts/UserProvider";
import { DeleteItem, Undo } from "../Icons";
import { useUserManagement } from "./UserManagement/UserManagementContext";
import styles from "./EditableListForm.module.css";

type ComponentProps = {
  closeFn: () => void;
};

type ItemProps = Group & {
  toggleDeleted: (id: string, isDeleted: boolean) => void;
};

const GroupsListItem = ({ name, id, toggleDeleted }: ItemProps) => {
  const [isDeleted, setIsDeleted] = useState(false);
  const deleteItem = () => {
    toggleDeleted(id || "", !isDeleted);
    setIsDeleted(!isDeleted);
  };

  return (
    <div className={isDeleted ? styles.deletedItem : undefined}>
      {name}
      {!!id && (
        <IconButton variant='muted' onClick={deleteItem}>
          {isDeleted ? <Undo /> : <DeleteItem />}
        </IconButton>
      )}
    </div>
  );
};

const GroupForm = ({ closeFn }: ComponentProps) => {
  const t = useTranslations("userManagementForm");
  const { groups, setGroups } = useUserManagement();
  const { currentOrganization } = useOrganizations();
  const { post, del } = useApiData();
  const [newGroups, setNewGroups] = useState<Group[]>([{ id: "", name: "", org: currentOrganization }]);
  const [deletedGroups, setDeletedGroups] = useState<Group[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const goBack = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    closeFn();
  };

  const handleGroupChange = (index: number, value: string) => {
    const grps: Group[] = [...newGroups];

    grps[index] = {
      ...grps[index],
      name: value,
    };

    if (index === grps.length - 1 && value) {
      grps.push({ id: "", name: "", org: currentOrganization });
    }
    setNewGroups(grps);
  };

  const handleBlur = (index: number) => {
    const { name } = newGroups[index];
    if (name.length < 1) {
      setErrors([...errors, { name: `group-${index}`, message: t("invalidGroup") }]);
    } else {
      setErrors(errors.filter((e) => e.name !== `group-${index}`));
    }
  };

  const toggleDeleted = (id: string, isDeleted: boolean) => {
    const department = groups.find((d) => d.id === id);
    if (department) {
      setDeletedGroups(isDeleted ? [...deletedGroups, department] : deletedGroups.filter((d) => d.id !== id));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = newGroups.filter(({ name }) => name !== "");

    if (data.length > 0) {
      const result = await post(`/organization/${currentOrganization}/groups`, data);
      if (result) {
        setGroups([...groups, ...result]);
        setNewGroups([{ id: "", name: "", org: currentOrganization }]);
      } else {
        setErrors([{ name: "form", message: t("misc.serverError") }]);
      }
    }
    if (deletedGroups.length > 0) {
      Promise.all(
        deletedGroups.map(async ({ id }) => {
          await del(`/organization/${currentOrganization}/groups/${id}`);
        }),
      ).then(() => {
        setGroups(groups.filter((d) => !deletedGroups.find((dg) => dg.id === d.id)));
        closeFn();
      });
    }
  };

  const closeLink = (
    <Link className={styles.closeLink} href='#' onClick={goBack}>
      {t("goBack")}
    </Link>
  );

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <H2>
        {t(groups.length === 0 ? "addGroup" : "editGroup")} {closeLink}
      </H2>
      <List
        items={groups.map((g: Group) => (
          <GroupsListItem key={g.id || g.name} toggleDeleted={toggleDeleted} {...g} />
        ))}
      />
      {newGroups.map(({ name }, index) => (
        <div className={styles.row} key={index}>
          <Input
            className={styles.inputContainer}
            error={errors.find((e) => e.name === `group-${index}`)}
            fieldClassName={styles.inputField}
            name={`group-${index}`}
            handleChange={(e: React.ChangeEvent<HTMLInputElement>) => handleGroupChange(index, e.target.value)}
            handleBlur={() => handleBlur(index)}
            label={t("groupNameLabel")}
            helpText={t("help.groupName")}
            value={name}
          />
        </div>
      ))}
      <Button>{t("submit")}</Button> {closeLink}
    </form>
  );
};

export default GroupForm;
